import { createStore } from 'vuex'
import errorResponse from '@/types/responses/errorResponse'
import { useAuth } from '@/services/user/Auth0UserProvider'
import axios from '@/services/api/providers/Connection'
import UserSession from '@/services/models/UserSession'
import AppSyncClient from '@/services/query/providers/Connection'

import { getVehicleInformations } from '@/services/VehicleService'

const store = createStore({
  state: {
    auth0Token: sessionStorage.getItem('auth0Token')
      ? JSON.parse(sessionStorage.getItem('auth0Token')!)
      : null,
    userSession: sessionStorage.getItem('userSession')
      ? JSON.parse(sessionStorage.getItem('userSession')!)
      : null,
    myOffice: sessionStorage.getItem('myOffice')
      ? JSON.parse(sessionStorage.getItem('myOffice')!)
      : null,
    selectableOperationOffices: localStorage.getItem(
      'selectableOperationOffices'
    )
      ? JSON.parse(localStorage.getItem('selectableOperationOffices')!)
      : null,
    selectedOperationOffice: localStorage.getItem('selectedOperationOffice')
      ? JSON.parse(localStorage.getItem('selectedOperationOffice')!)
      : null,
    searchedOperationOffices: sessionStorage.getItem('searchedOperationOffices')
      ? JSON.parse(sessionStorage.getItem('searchedOperationOffices')!)
      : [],
    isStoredVehiclesResult: false,
    vehiclesResult: null,
    latestVehicleFetchRequestId: 0,
    isRefreshing: false,
    refreshSubscribers: [] as Array<(newToken: string) => void>,
  },
  mutations: {
    setUserSession(state, sessionData) {
      state.userSession = sessionData
      sessionStorage.setItem('userSession', JSON.stringify(sessionData))
    },
    clearUserSession(state) {
      state.userSession = null
      sessionStorage.removeItem('userSession')
    },
    setAuth0Token(state, auth0Data) {
      state.auth0Token = auth0Data
      sessionStorage.setItem('auth0Token', JSON.stringify(auth0Data))
    },
    clearAuth0Token(state) {
      state.auth0Token = null
      sessionStorage.removeItem('auth0Token')
    },
    setAuth0Refreshing(state, isRefreshing) {
      state.isRefreshing = isRefreshing
    },
    addAuth0Subscriber(state, callback) {
      state.refreshSubscribers.push(callback)
    },
    addRefreshSubscriber(state, subscriber: (newToken: string) => void) {
      state.refreshSubscribers.push(subscriber)
    },
    runSubscribers(state, newToken: string) {
      state.refreshSubscribers.forEach((callback) => callback(newToken))
      state.refreshSubscribers = []
    },
    setMyOffice(state, myOffice) {
      state.myOffice = myOffice
      sessionStorage.setItem('myOffice', JSON.stringify(myOffice))
    },
    clearMyOffice(state) {
      state.myOffice = null
      sessionStorage.removeItem('myOffice')
    },
    setSelectableOperationOffices(state, selectableOperationOffices) {
      state.selectableOperationOffices = selectableOperationOffices
      localStorage.setItem(
        'selectableOperationOffices',
        JSON.stringify(selectableOperationOffices)
      )
    },
    clearSelectableOperationOffices(state) {
      state.selectableOperationOffices = []
      localStorage.removeItem('selectableOperationOffices')
    },
    setSelectedOperationOffice(state, selectedOperationOffice) {
      state.selectedOperationOffice = selectedOperationOffice
      localStorage.setItem(
        'selectedOperationOffice',
        JSON.stringify(selectedOperationOffice)
      )
    },
    clearSelectedOperationOffice(state) {
      state.selectedOperationOffice = null
      localStorage.removeItem('selectedOperationOffice')
    },
    setSearchedOperationOffices(state, searchedOperationOffices) {
      state.searchedOperationOffices = searchedOperationOffices
      sessionStorage.setItem(
        'searchedOperationOffices',
        JSON.stringify(searchedOperationOffices)
      )
    },
    clearSearchedOperationOffices(state) {
      state.searchedOperationOffices = []
      sessionStorage.removeItem('searchedOperationOffices')
    },
    setVehiclesResult(state, vehiclesResult) {
      state.vehiclesResult = vehiclesResult
    },
    setIsStoredVehiclesResult(state, isStore) {
      state.isStoredVehiclesResult = isStore
    },
    setLatestVehicleFetchRequestId(state, id: number) {
      state.latestVehicleFetchRequestId = id
    },
  },
  actions: {
    fetchVehiclesResult({ commit, state }, operationOfficeIds) {
      commit('setIsStoredVehiclesResult', false)
      const requestId = Date.now()
      commit('setLatestVehicleFetchRequestId', requestId)
      return (async () => {
        await getVehicleInformations((vehicles: any) => {
          // レースコンディションが発生した時に、前の検索結果で上書きされるのを防ぐ
          if (state.latestVehicleFetchRequestId === requestId) {
            commit('setVehiclesResult', { vehicles: vehicles })
          }
        }, operationOfficeIds).catch((error: errorResponse) => {
          console.log(error)
          throw error
        })
        if (state.latestVehicleFetchRequestId === requestId) {
          commit('setIsStoredVehiclesResult', true)
        }
      })()
    },
    async refreshToken({ state, commit }) {
      if (state.isRefreshing) {
        // 更新中なら Promise を返す
        return new Promise<string>((resolve) => {
          commit('addRefreshSubscriber', resolve)
        })
      }

      const auth0 = useAuth()
      if (auth0.isAuthenticated.value) {
        // tokenを取得
        auth0.getTokenSilently().then((token) => {
          // Bearer Tokenを設定
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
          // Vuex保存
          store.commit('setAuth0Token', token)

          // UserSession Tokenを設定
          UserSession.get().then((response) => {
            if (response?.data) {
              // Vuex保存
              store.commit('setUserSession', response?.data)
              store.commit('runSubscribers', response?.data)
              store.commit('setAuth0Refreshing', false)
              AppSyncClient.token = token
            }
          })
        })
      }
    },
    addRefreshSubscriber({ commit }, callback) {
      commit('addAuth0Subscriber', callback)
    },
  },
  getters: {
    plainUserSession: (state) => JSON.parse(JSON.stringify(state.userSession)),
    plainAuth0Token: (state) => JSON.parse(JSON.stringify(state.auth0Token)),
    plainMyOffice: (state) => JSON.parse(JSON.stringify(state.myOffice)),
    plainSelectableOperationOffices: (state) =>
      JSON.parse(JSON.stringify(state.selectableOperationOffices)),
    plainSelectedOperationOffice: (state) =>
      JSON.parse(JSON.stringify(state.selectedOperationOffice)),
    plainSearchedOperationOffices: (state) =>
      JSON.parse(JSON.stringify(state.searchedOperationOffices)),
  },
})

export default store
